import { AuthenticationService } from '@/services/authentication.service';
import { MenuToNativeId } from '@/services/bridge/bridge.service';
import { UserRightsService } from '@/services/user-rights.service';
import { Injectable } from '@angular/core';
import {
  AccountType,
  CertificationType,
  Document,
  IdentityProviderType,
  PropertyType,
  RealProperty,
  SharingDataCategory,
  SubscriptionState,
  User,
  UserRightType,
  UserRole,
  UserSession,
  UserSummary
} from '@eemo/api-client';
import { UserSummaryDetail } from '@eemo/api-client/model/userSummaryDetail';
import { Utils } from './utils';

@Injectable({ providedIn: 'root' })
export class UserHelper {
  static readonly DEFAULT_AVATAR_TS = '1';
  currentUser: UserSession;
  currentSubscriptionState: SubscriptionState;

  constructor(private authenticationService: AuthenticationService, private userRightsService: UserRightsService) {
    this.authenticationService.currentUser.subscribe((value) => {
      if (value) {
        this.currentUser = value;
      }
    });
    this.authenticationService.subscriptionState.subscribe((s) => {
      if (s) {
        this.currentSubscriptionState = s;
      }
    });
  }

  public get displayName(): string {
    return this.currentUser.firstName + ' ' + this.currentUser.name;
  }

  public get email(): string {
    return this.currentUser.email;
  }

  public static userDisplayName(user: User, withCompany = false): string {
    if (!user) {
      return '';
    }
    let company = '';
    if (withCompany && user.companies?.length > 0) {
      // add the main company
      company = ' (' + user.companies.find((a) => a.main).company.name + ')';
    }
    if (user.firstName && user.name) {
      return user.firstName + ' ' + user.name + company;
    } else if (user.firstName && !user.name) {
      return user.firstName + company;
    } else if (!user.firstName && user.name) {
      return user.name + company;
    } else {
      return user.uid;
    }
  }

  public static userSummmaryDisplayName(user: UserSummary | UserSummaryDetail): string {
    if (!user) {
      return '';
    }
    if (user.firstName && user.name) {
      return user.firstName + ' ' + user.name;
    } else if (user.firstName && !user.name) {
      return user.firstName;
    } else if (!user.firstName && user.name) {
      return user.name;
    } else {
      return user.uid;
    }
  }

  public static getUserInitials(user: User) {
    return (user.firstName.charAt(0) + user.name.charAt(0)).toUpperCase();
  }

  public static getUserSummaryInitials(user: UserSummary) {
    return (user.firstName.charAt(0) + user.name.charAt(0)).toUpperCase();
  }

  public isCurrentSubscriptionActive(): boolean {
    return UserHelper.isSubscriptionActive(this.currentSubscriptionState);
  }

  public static isSubscriptionActive(state: SubscriptionState): boolean {
    return state === SubscriptionState.ACTIVE || state === SubscriptionState.TRIALING;
  }

  public static isSubscriptionAboutToExpire(expirationDate: Date): boolean {
    return Utils.getDatesDiffInDays(new Date(), expirationDate) < 7;
  }

  getRoleName(role: UserRole): string {
    switch (role) {
      case UserRole.INDIVIDUAL:
        return 'Particulier';
      case UserRole.PROFESSIONAL:
        return 'Professionnel';
      case UserRole.REAL_ESTATE:
        return 'Agent immobilier';
      case UserRole.NOTARY:
        return 'Notaire';
      case UserRole.COOP_BOARD:
        return 'Syndic';
      case UserRole.RESCUE_SERVICE:
        return 'Service de secours';
      case UserRole.EEMO:
        return 'Team EEMO';
      case UserRole.ADMIN:
        return 'Administrateur';
      default:
        return 'Rôle inconnu';
    }
  }

  getAllowedRoleForRegister(): UserRole[] {
    return [UserRole.INDIVIDUAL, UserRole.REAL_ESTATE, UserRole.NOTARY, UserRole.PROFESSIONAL];
  }

  isRolePremiumOnly(role: UserRole): boolean {
    return (
      UserRole.ADMIN === role ||
      UserRole.EEMO === role ||
      UserRole.REAL_ESTATE === role ||
      UserRole.NOTARY === role ||
      UserRole.RESCUE_SERVICE === role
    );
  }

  isAccountTypeFree(type: AccountType): boolean {
    return AccountType.FREE === type;
  }

  isAccountTypePremium(type: AccountType): boolean {
    return AccountType.PREMIUM === type;
  }

  canRoleAddCompanyName(role: UserRole): boolean {
    return UserRole.INDIVIDUAL !== role;
  }

  canAddCompanyName(): boolean {
    return !this.isIndividual();
  }

  canAccessPropertyCreateFull(): boolean {
    return this.isAdmin() || this.isRealEstate();
  }

  isManagedExternally(user: User): boolean {
    return user.idp !== IdentityProviderType.B2C;
  }

  isAdmin(): boolean {
    return this.currentUser && UserRole.ADMIN === this.currentUser.role;
  }

  isEEMO(): boolean {
    return this.currentUser && UserRole.EEMO === this.currentUser.role;
  }

  isTeamEEMO(): boolean {
    return this.isAdmin() || this.isEEMO();
  }

  isRescueService(): boolean {
    return this.currentUser && UserRole.RESCUE_SERVICE === this.currentUser.role;
  }

  isIndividual(): boolean {
    return this.currentUser && UserRole.INDIVIDUAL === this.currentUser.role;
  }

  isProfessional(): boolean {
    return this.currentUser && UserRole.PROFESSIONAL === this.currentUser.role;
  }

  isRealEstate(): boolean {
    return this.currentUser && UserRole.REAL_ESTATE === this.currentUser.role;
  }

  isRealEstateKw(): boolean {
    return (
      this.currentUser &&
      UserRole.REAL_ESTATE === this.currentUser.role &&
      this.currentUser.email.endsWith('@kwfrance.com')
    );
  }

  isNotary(): boolean {
    return this.currentUser && UserRole.NOTARY === this.currentUser.role;
  }

  isCoopBoard(): boolean {
    return this.currentUser && UserRole.COOP_BOARD === this.currentUser.role;
  }

  isMapSearchEnabled(): boolean {
    return this.isAdmin() || this.isEEMO() || this.isRescueService();
  }

  isMapLocateEnabled(): boolean {
    return this.isAdmin() || this.isEEMO() || this.isRescueService();
  }

  isPropertyOwner(prop: RealProperty): boolean {
    return (
      this.currentUser &&
      (this.currentUser.userId === prop.owner.uid ||
        (prop.coOwner && this.currentUser.userId === prop.coOwner.uid) ||
        prop.userRightType === UserRightType.OWNER)
    );
  }

  canAddProperty(): boolean {
    if (!UserHelper.isSubscriptionActive(this.currentSubscriptionState)) {
      return false;
    }
    return !this.isProfessional() && !this.isCoopBoard() && !this.isRescueService();
  }

  hasReachedPropertyLimit(props: RealProperty[]) {
    return this.isAccountTypeFree(this.currentUser.accountType) && props && props.length >= 1;
  }

  canUpdateProperty(prop: RealProperty): boolean {
    if (!UserHelper.isSubscriptionActive(this.currentSubscriptionState)) {
      return false;
    }
    return (
      UserRole.ADMIN === this.currentUser.role ||
      prop.userRightType === UserRightType.OWNER ||
      prop.userRightType === UserRightType.DELEGATE
    );
  }

  canViewCategory(prop: RealProperty, category: SharingDataCategory) {
    if (prop.type === PropertyType.CONDOMINIUM) {
      // implicit view right for condo
      return true;
    }
    return (
      this.canUpdateProperty(prop) ||
      // manage the case of a real estate user with expired subscription
      this.isPropertyOwner(prop) ||
      (prop.details?.accessSharing &&
        (!prop.details.accessSharing.allowedData || prop.details.accessSharing.allowedData.length === 0) &&
        SharingDataCategory.SHARING !== category &&
        SharingDataCategory.PEOPLE !== category) ||
      prop.details?.accessSharing?.allowedData?.includes(category) ||
      this.userRightsService.hasViewRightOnCategory(prop.uid, category)
    );
  }

  canTransfertProperty(prop: RealProperty): boolean {
    if (!UserHelper.isSubscriptionActive(this.currentSubscriptionState)) {
      return false;
    }
    return (
      UserRole.ADMIN === this.currentUser.role ||
      ((UserRole.REAL_ESTATE === this.currentUser.role || UserRole.NOTARY === this.currentUser.role) &&
        this.isPropertyOwner(prop))
    );
  }

  canCertifyProperty(prop: RealProperty): boolean {
    return (
      UserRole.ADMIN === this.currentUser.role ||
      (UserRole.REAL_ESTATE === this.currentUser.role && this.canUpdateProperty(prop))
    );
  }

  canAddPropertyShare(prop: RealProperty): boolean {
    return (UserRole.ADMIN === this.currentUser.role || this.canUpdateProperty(prop)) && prop.certified;
  }

  canUpdateDocument(prop: RealProperty, doc: Document): boolean {
    return this.isAdmin() || (this.canUpdateProperty(prop) && (!doc.auto || this.isRealEstate()));
  }

  canDeleteDocument(prop: RealProperty, doc: Document): boolean {
    return this.isAdmin() || (this.canUpdateProperty(prop) && ((!doc.auto && !doc.certified) || this.isRealEstate()));
  }

  canIdentifyDocument(prop: RealProperty): boolean {
    return (
      UserRole.ADMIN === this.currentUser.role ||
      (UserRole.REAL_ESTATE === this.currentUser.role && this.canUpdateProperty(prop))
    );
  }

  canDeleteProperty(prop: RealProperty): boolean {
    if (!UserHelper.isSubscriptionActive(this.currentSubscriptionState)) {
      return false;
    }
    return (
      this.isAdmin() ||
      ((prop.owner.uid === this.currentUser.userId || (prop.coOwner && prop.coOwner.uid === this.currentUser.userId)) &&
        (!prop.certified ||
          (CertificationType.PROFESSIONAL === prop.certificationType &&
            UserRole.REAL_ESTATE === this.currentUser.role)))
    );
  }

  canArchiveProperty(prop: RealProperty): boolean {
    return this.isAdmin() || ((this.isRealEstate() || this.isNotary()) && this.isPropertyOwner(prop));
  }

  canManagePropertyService(prop: RealProperty): boolean {
    //return this.isAdmin() || (this.isRealEstate() && this.canUpdateProperty(prop));
    // service management si temporaly restricted to admin
    return this.isAdmin();
  }

  canAddPropertySale(prop: RealProperty): boolean {
    return (
      !prop.onGoingSelling &&
      (this.isAdmin() || ((this.isRealEstate() || this.isNotary()) && this.canUpdateProperty(prop)))
    );
  }

  canManagePropertySale(prop: RealProperty): boolean {
    return (
      prop.onGoingSelling &&
      (this.isAdmin() || ((this.isRealEstate() || this.isNotary()) && this.canUpdateProperty(prop)))
    );
  }

  canViewPropertyDataSync(prop: RealProperty): boolean {
    return prop.certified && (this.isAdmin() || this.isRealEstate() || this.isNotary()) && this.canUpdateProperty(prop);
  }

  canDownloadDocProperty(): boolean {
    return (
      this.isAdmin() || this.isNotary() || this.isEEMO() || (this.isRealEstate() && this.isCurrentSubscriptionActive())
    );
  }

  public userSessionDisplayName(user: UserSession): string {
    return user.firstName + ' ' + user.name;
  }

  canViewCategoryByMenuId(prop: RealProperty, menuItemId: MenuToNativeId): boolean {
    let category: SharingDataCategory;
    switch (menuItemId) {
      // special case infos
      case MenuToNativeId.PROP_INFO:
        return (
          this.canViewCategory(prop, SharingDataCategory.LOCATION) ||
          this.canViewCategory(prop, SharingDataCategory.BUILDING) ||
          this.canViewCategory(prop, SharingDataCategory.ENERGY) ||
          this.canViewCategory(prop, SharingDataCategory.FINANCIAL) ||
          this.canViewCategory(prop, SharingDataCategory.PEOPLE) ||
          this.canViewCategory(prop, SharingDataCategory.CONDOMINIUM) ||
          this.canViewCategory(prop, SharingDataCategory.SHARING)
        );
      // special case tracking
      case MenuToNativeId.TRACKING:
        return (
          this.canViewCategory(prop, SharingDataCategory.TIMELINE) ||
          this.canViewCategory(prop, SharingDataCategory.CONSUMPTION) ||
          this.canViewCategory(prop, SharingDataCategory.MAINTENANCE) ||
          this.canViewCategory(prop, SharingDataCategory.WORK) ||
          this.canViewCategory(prop, SharingDataCategory.FINANCIAL)
        );
      case MenuToNativeId.PROP_SHARES:
        category = SharingDataCategory.SHARING;
        break;
      case MenuToNativeId.PHOTO:
        category = SharingDataCategory.PHOTO;
        break;
      case MenuToNativeId.LOCATION:
        category = SharingDataCategory.LOCATION;
        break;
      case MenuToNativeId.BUILDING:
        category = SharingDataCategory.BUILDING;
        break;
      case MenuToNativeId.ENERGY:
        category = SharingDataCategory.ENERGY;
        break;
      case MenuToNativeId.PEOPLE:
        category = SharingDataCategory.PEOPLE;
        break;
      case MenuToNativeId.TIMELINE:
        category = SharingDataCategory.TIMELINE;
        break;
      case MenuToNativeId.CONSUMPTION:
        category = SharingDataCategory.CONSUMPTION;
        break;
      case MenuToNativeId.MAINTENANCE:
        category = SharingDataCategory.MAINTENANCE;
        break;
      case MenuToNativeId.WORK:
        category = SharingDataCategory.WORK;
        break;
      case MenuToNativeId.FINANCIAL:
      case MenuToNativeId.TRACKING_FINANCIAL:
        category = SharingDataCategory.FINANCIAL;
        break;
      default:
        break;
    }

    return this.canViewCategory(prop, category);
  }

  hasDefaultAvatar(user: User | UserSummary): boolean {
    return user.avatarTs === UserHelper.DEFAULT_AVATAR_TS;
  }
}
